import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApolloLink } from '@apollo/client/core';
import { LogService } from '@innovation-os-ng/core/logger';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { FULLY_UNAUTHENTICATED_APIS, PARTIALLY_UNAUTHENTICATED_APIS } from '../constants/unauthenticated-apis';
import { SessionService } from '../contracts/session-service.contract';

/**
 * HTTP Interceptor to add an Authorization header to all outgoing
 * request, if a token is set (i.e. the user is logged in).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthHeaderInterceptor implements HttpInterceptor {
  /**
   * Creates a new instance of the interceptor and provides services.
   *
   * @param sessionService Used to retrieve the token from the user's cookies
   */
  constructor(private readonly sessionService: SessionService, private readonly logger: LogService) {}

  /**
   * Adds the Authorization header if a user token is set in the cookies.
   *
   * @param request The original outgoing request
   * @param next The next HTTP handler to call with the original or modified request
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Exclude the API call to refresh the token, as otherwise, this would
    // trigger another round of interception and a MaxCallStackSizeExceeded error.
    if (this.isUnauthenticatedApi(request)) {
      return next.handle(request);
    }

    const token = this.sessionService.getToken();
    if (!token || token === 'undefined') {
      this.logger.info('AuthHeaderInterceptor used without stored token. Request will be queued!', {
        tokenValue: token,
        tokenType: typeof token,
        requestUrl: request.url,
        requestUrlWithParams: request.urlWithParams
      });
    }
    // Queue the request until the auth token is ready.
    return this.sessionService.onAuthReady.pipe(
      // The rest request needs to be send only once. Do not keep subscribed
      take(1),
      // Get the token from the onAuthReady
      switchMap((readyToken) => {
        // Pass the cloned request instead of the original request to the next handle
        const newRequest = request.clone({
          setHeaders: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Authorization: 'Bearer ' + readyToken
          },
          withCredentials: true
        });
        return next.handle(newRequest);
      })
    );
  }

  /**
   * Provides a HttpLink for Apollo, which adds the Authorization headers
   * to outgoing requests.
   */
  public getAuthLink(): ApolloLink {
    return new ApolloLink((operation, forward) => {
      // Retrieve the authorization token from local storage.
      const token = this.sessionService.getToken();

      // Use the setContext method to set the HTTP headers.
      operation.setContext({
        headers: {
          authorization: `Bearer ${token}`
        }
      });

      // Call the next link in the middleware chain.
      return forward(operation);
    });
  }

  private isUnauthenticatedApi(request: HttpRequest<any>) {
    return (
      Object.values(FULLY_UNAUTHENTICATED_APIS).reduce((result, apiPath) => result || request.url.includes(apiPath), false) ||
      Object.values(PARTIALLY_UNAUTHENTICATED_APIS).reduce((result, apiCheck) => result || apiCheck(request.url), false)
    );
  }
}
